<template>
  <v-tooltip
    location="top"
    :disabled="!buttonDisabled"
    :text="tooltipText"
  >
    <template #activator="{ props }">
      <div v-bind="props">
        <slot
          name="button"
          :on-click="() => (showDialog = true)"
          :disabled="buttonDisabled"
        >
          <np-action-btn
            :disabled="buttonDisabled"
            class="float-right"
            @click="showDialog = true"
          >
            {{ label }}
          </np-action-btn>
        </slot>
        <UpsertSharedMapDialog
          :show-dialog="showDialog"
          :max-items="maxItems"
          :export-message="exportMessage"
          :total-items="totalItems"
          :operations="operations"
          :collections="collections"
          @close="showDialog = false"
        />
      </div>
    </template>
  </v-tooltip>
</template>

<script lang="ts">
// Services and Helpers
import {PropType} from 'vue';
import {AcceptedOperation} from './UpsertSharedMap';

// Components
import UpsertSharedMapDialog from './UpsertSharedMapDialog.vue';
import {getCollection} from '@/lib/filters/filters';

export default {
  name: 'UpsertSharedMap',
  components: {
    UpsertSharedMapDialog,
  },
  props: {
    /** The maximum number of items this instance can export. */
    maxItems: {
      type: Number,
      default: Number.POSITIVE_INFINITY,
    },
    /** An array defining the operations this instance can perform. */
    operations: {
      type: Array as PropType<AcceptedOperation[]>,
      default: () => ['add', 'create'],
    },
    /** An array defining the collections used to list exportable items. */
    collections: {
      type: Array as PropType<string[]>,
      required: true,
    },
    /** The label to give the button for opening the dialog. */
    label: {
      type: String,
      default: 'Build Shared Map',
    },
  },
  data() {
    return {
      showDialog: false,
      totalItems: 0,
    };
  },
  computed: {
    buttonDisabled() {
      return this.totalItems > this.maxItems || this.totalItems === 0;
    },
    exportMessage() {
      if (process.env.VUE_APP_GCP_PROJECT_ID!.includes('shareablemaps')) {
        return `Export all ${this.totalItems} items from this map. Choose the fields to include:`;
      }

      return `You selected ${this.totalItems} items. Choose the export type and fields to include:`;
    },
    tooltipText() {
      if (this.totalItems === 0) {
        return 'No items to export.';
      }
      return `There are ${this.totalItems} items selected. The limit is ${this.maxItems} items.`;
    },
  },
  beforeMount() {
    this.collections.forEach((colName) => {
      const collection = getCollection(colName);
      if (!collection) {
        console.warn(`Collection "${colName}" not found.`);
        return;
      }

      this.totalItems += (collection.getFiltered(false) as any[]).length;

      collection.subscribe(null, ({payload}: {payload: {added: any[]; removed: any[]}}) => {
        const numberAdded = payload.added?.length || 0;
        const numberRemoved = payload.removed?.length || 0;
        this.totalItems += (numberAdded - numberRemoved);
      });
    });
  },
};
</script>
